import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';

const faqs = [
  {
    question: '草灯和尚是一个真实的历史人物吗？',
    answer: '草灯和尚是一个结合了历史传说与禅宗智慧的文化符号。其形象源自唐代深山古寺中以草为灯的僧人故事，经过千年传承和艺术加工，逐渐成为东方禅意文化的重要象征。',
  },
  {
    question: '如何理解"以草为灯、以心照路"的含义？',
    answer: '这句话是草灯和尚的核心精神。"以草为灯"象征在最简朴的条件下也能找到光明；"以心照路"则强调以内心的觉悟和智慧来指引人生方向。合在一起，表达了禅宗"明心见性"的根本教义。',
  },
  {
    question: '草灯和尚的修行哲学适合现代人吗？',
    answer: '非常适合。草灯和尚提倡的简朴之道、明心见性、天人合一、日用即禅等理念，恰恰是应对现代生活压力和焦虑的良方。无论是通过静坐、品茶还是日常生活中的专注投入，都可以实践草灯智慧。',
  },
  {
    question: '如何订阅草灯和尚的文化资讯？',
    answer: '您可以在网站首页底部的订阅区域输入邮箱地址，我们将定期为您推送草灯和尚的禅意故事、修行哲学与生活美学内容。',
  },
  {
    question: '草灯和尚有哪些艺术表现形式？',
    answer: '草灯和尚的艺术形象经历了丰富的演变，从唐代的寺庙壁画到宋元的文人水墨，再到当代的插画、雕塑、文创产品设计等。每一种形式都在用自己的方式诠释这位禅意人物的精神内涵。',
  },
];

const aboutSchema = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'WebPage',
      '@id': 'https://caodenghoshang.com/about#webpage',
      url: 'https://caodenghoshang.com/about',
      name: '关于草灯和尚 | 品牌介绍·口碑声誉·联系我们',
      isPartOf: { '@id': 'https://caodenghoshang.com/#website' },
      inLanguage: 'zh-Hans',
    },
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'https://caodenghoshang.com/' },
        { '@type': 'ListItem', position: 2, name: '关于我们' },
      ],
    },
    {
      '@type': 'FAQPage',
      mainEntity: faqs.map((faq) => ({
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.answer,
        },
      })),
    },
  ],
};

export default function AboutPage() {
  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutSchema) }}
      />
      <ScrollToTop />
      <Navbar />
      <main>
        {/* Hero */}
        <section className="pt-28 pb-16 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <nav className="flex items-center gap-2 text-sm text-foreground-400 mb-6">
              <Link to="/" className="hover:text-primary-600 transition-colors">首页</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-700">关于我们</span>
            </nav>
            <div className="max-w-3xl">
              <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-900 mb-4">
                关于草灯和尚
              </h1>
              <p className="text-foreground-600 leading-relaxed text-base">
                草灯和尚是一个承载禅宗智慧与草木灵性的文化品牌，
                致力于传播东方生活美学与心灵修行之道，让传统禅意文化在现代社会焕发新的生命力。
              </p>
            </div>
          </div>
        </section>

        {/* Brand Story */}
        <section className="py-16 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
              <div className="lg:w-1/2">
                <div className="relative w-full aspect-[4/3] rounded-lg overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Traditional%20Chinese%20zen%20study%20room%20with%20calligraphy%20scrolls%20on%20wall%2C%20antique%20wooden%20desk%20with%20ink%20stone%20and%20brush%2C%20a%20simple%20grass%20lantern%20glowing%20warmly%2C%20paper%20window%20with%20soft%20light%2C%20serene%20scholarly%20atmosphere%2C%20warm%20earthy%20tones&width=800&height=600&seq=about-story-01&orientation=landscape"
                    alt="草灯和尚 - 品牌故事"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              <div className="lg:w-1/2">
                <p className="text-primary-600 text-sm font-medium tracking-wider mb-3">品牌故事</p>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-6">
                  一盏草灯，千年禅心
                </h2>
                <p className="text-foreground-600 leading-relaxed mb-4">
                  草灯和尚的文化源头可以追溯到唐代。相传在深山古寺中，有一位僧人以山间枯草编织成灯，
                  在微弱的光芒中坚持诵读经卷。他以最朴素的方式，守护着内心的光明与智慧。
                </p>
                <p className="text-foreground-600 leading-relaxed mb-4">
                  这个故事在民间流传千年，草灯和尚逐渐成为一个承载东方智慧的独特文化符号。
                  草灯象征着每个人心中本具的觉悟之光——即便在最简陋的条件下，只要点燃心灯，就能照亮前行的道路。
                </p>
                <p className="text-foreground-600 leading-relaxed">
                  今天，我们以"草灯和尚"为名，致力于挖掘、整理和传播这份珍贵的文化遗产。
                  通过禅意故事、修行哲学、生活美学和艺术形象等多个维度，
                  让古老的东方智慧走进现代人的生活，在一草一木、一举一动中体悟禅的真谛。
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Mission & Vision */}
        <section className="py-16 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center p-8 rounded-lg border border-secondary-200/70 bg-background-50">
                <div className="w-14 h-14 mx-auto mb-5 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                  <i className="ri-lightbulb-flash-line text-2xl"></i>
                </div>
                <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-3">我们的使命</h3>
                <p className="text-sm text-foreground-500 leading-relaxed">
                  传承和弘扬以草灯和尚为代表的东方禅意文化，
                  让更多人在现代生活中找到内心的宁静与智慧，
                  以草为灯，以心照路。
                </p>
              </div>
              <div className="text-center p-8 rounded-lg border border-secondary-200/70 bg-background-50">
                <div className="w-14 h-14 mx-auto mb-5 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                  <i className="ri-eye-line text-2xl"></i>
                </div>
                <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-3">我们的愿景</h3>
                <p className="text-sm text-foreground-500 leading-relaxed">
                  成为全球最具影响力的东方禅意文化平台，
                  搭建传统智慧与现代生活之间的桥梁，
                  让草灯的光芒照亮世界的每一个角落。
                </p>
              </div>
              <div className="text-center p-8 rounded-lg border border-secondary-200/70 bg-background-50">
                <div className="w-14 h-14 mx-auto mb-5 flex items-center justify-center rounded-full bg-secondary-200 text-secondary-700">
                  <i className="ri-heart-line text-2xl"></i>
                </div>
                <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-3">我们的价值观</h3>
                <p className="text-sm text-foreground-500 leading-relaxed">
                  以真诚之心传承文化，以简朴之道修行人生，
                  以开放之态拥抱现代，以敬畏之情尊重自然，
                  在平凡日常中践行禅的智慧。
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="py-16 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <p className="text-primary-600 text-sm font-medium tracking-wider mb-3">口碑声誉</p>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-4">
                读者心声
              </h2>
              <p className="text-foreground-500 max-w-xl mx-auto text-sm leading-relaxed">
                来自读者和修行者的真实反馈，见证草灯文化的力量
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                { name: '林女士', role: '都市白领，禅修爱好者', bgClass: 'bg-accent-100', textClass: 'text-accent-700', text: '草灯和尚的文章让我在繁忙的都市生活中找到了片刻的宁静。每次阅读都像是做了一次心灵的深呼吸，让我重新找回内心的平衡。' },
                { name: '王教授', role: '文化研究学者', bgClass: 'bg-primary-100', textClass: 'text-primary-700', text: '作为一个研究东方文化的学者，我认为草灯和尚的内容既有学术深度又有生活温度。它让传统的禅宗智慧变得触手可及。' },
                { name: '陈先生', role: '设计师，生活美学实践者', bgClass: 'bg-secondary-200', textClass: 'text-secondary-700', text: '草灯和尚的生活美学理念改变了我的生活方式。从一杯茶到一盆花，生活中的每一个细节都变得有意义了。' },
              ].map((t) => (
                <div key={t.name} className="p-6 rounded-lg border border-secondary-200/70 bg-background-50">
                  <div className="flex gap-1 mb-4 text-accent-500">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <i key={i} className="ri-star-fill text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-5">
                    &ldquo;{t.text}&rdquo;
                  </p>
                  <div className="flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-full ${t.bgClass} flex items-center justify-center`}>
                      <span className={`text-xs font-semibold ${t.textClass}`}>{t.name[0]}</span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">{t.name}</p>
                      <p className="text-xs text-foreground-400">{t.role}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 md:py-20 bg-background-100">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <p className="text-primary-600 text-sm font-medium tracking-wider mb-3">常见问题</p>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-4">
                了解更多
              </h2>
              <p className="text-foreground-500 max-w-xl mx-auto text-sm leading-relaxed">
                关于草灯和尚的常见疑问，在这里找到答案
              </p>
            </div>

            <div className="space-y-4">
              {faqs.map((faq, idx) => (
                <details key={idx} className="group bg-background-50 rounded-lg border border-secondary-200/70">
                  <summary className="flex items-center justify-between p-5 cursor-pointer list-none">
                    <span className="text-sm font-medium text-foreground-900 pr-4">{faq.question}</span>
                    <span className="w-6 h-6 flex items-center justify-center shrink-0 rounded-full bg-secondary-100 text-secondary-500 group-open:bg-primary-100 group-open:text-primary-600 transition-colors">
                      <i className="ri-add-line group-open:hidden text-sm"></i>
                      <i className="ri-subtract-line hidden group-open:block text-sm"></i>
                    </span>
                  </summary>
                  <div className="px-5 pb-5">
                    <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <ContactSection />
      </main>
      <Footer />
    </>
  );
}

function ContactSection() {
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formEl = e.currentTarget;
    const formData = new FormData(formEl);
    const honeypot = (formData.get('company_alt') as string || '').trim();
    if (honeypot) {
      setStatus('success');
      setForm({ name: '', email: '', subject: '', message: '' });
      return;
    }

    if (form.message.length > 500) {
      setStatus('error');
      setErrorMsg('留言内容不能超过500个字符');
      return;
    }

    setStatus('loading');
    setErrorMsg('');

    try {
      const submitData = new FormData();
      submitData.append('name', form.name);
      submitData.append('email', form.email);
      submitData.append('subject', form.subject);
      submitData.append('message', form.message);
      const response = await fetch('https://readdy.ai/api/form/d9q6nhcnlsngrm4llsgg', {
        method: 'POST',
        body: submitData,
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setStatus('success');
        setForm({ name: '', email: '', subject: '', message: '' });
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        setStatus('error');
        setErrorMsg(serverMsg || '提交失败，请稍后再试');
      }
    } catch {
      setStatus('error');
      setErrorMsg('网络错误，请检查网络连接后重试');
    }
  };

  return (
    <section className="py-16 md:py-20 bg-background-50">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex flex-col lg:flex-row gap-10 lg:gap-16">
          <div className="lg:w-2/5">
            <p className="text-primary-600 text-sm font-medium tracking-wider mb-3">联系我们</p>
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-4">
              与草灯结缘
            </h2>
            <p className="text-foreground-500 text-sm leading-relaxed mb-8">
              如果您对草灯和尚的文化内容有任何建议、合作意向或修行心得想要分享，
              欢迎通过以下方式与我们联系。
            </p>

            <div className="space-y-5">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 shrink-0">
                  <i className="ri-mail-line text-lg"></i>
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground-900">电子邮箱</p>
                  <p className="text-sm text-foreground-500">caodenghoshang@culture.org</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 shrink-0">
                  <i className="ri-wechat-line text-lg"></i>
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground-900">微信公众号</p>
                  <p className="text-sm text-foreground-500">草灯和尚禅意文化</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-secondary-200 text-secondary-700 shrink-0">
                  <i className="ri-map-pin-line text-lg"></i>
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground-900">地址</p>
                  <p className="text-sm text-foreground-500">浙江省杭州市西湖区灵隐路18号</p>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:w-3/5">
            {status === 'success' ? (
              <div className="bg-background-50 rounded-lg border border-accent-200 p-10 text-center">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                  <i className="ri-check-line text-2xl"></i>
                </div>
                <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-2">提交成功</h3>
                <p className="text-sm text-foreground-500">感谢您的留言，我们会尽快与您联系。</p>
              </div>
            ) : (
              <form
                data-readdy-form
                id="contact-form"
                onSubmit={handleSubmit}
                className="bg-background-50 rounded-lg border border-secondary-200/70 p-6 md:p-8"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      姓名 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      id="contact-name"
                      type="text"
                      name="name"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-secondary-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-all"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      邮箱 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      id="contact-email"
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-secondary-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-all"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                </div>
                <div className="mb-4">
                  <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    主题 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    id="contact-subject"
                    type="text"
                    name="subject"
                    value={form.subject}
                    onChange={(e) => setForm({ ...form, subject: e.target.value })}
                    required
                    className="w-full px-4 py-2.5 rounded-lg border border-secondary-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-all"
                    placeholder="请输入主题"
                  />
                </div>
                <div className="mb-4">
                  <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    留言 <span className="text-primary-500">*</span>
                    <span className="text-foreground-400 font-normal ml-2">(最多500字)</span>
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    required
                    maxLength={500}
                    rows={5}
                    className="w-full px-4 py-2.5 rounded-lg border border-secondary-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-all resize-none"
                    placeholder="请输入您的留言内容..."
                  ></textarea>
                  <p className="text-xs text-foreground-400 mt-1">{form.message.length}/500</p>
                </div>
                <input
                  type="text"
                  name="company_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="absolute opacity-0 pointer-events-none"
                  style={{ position: 'absolute', left: '-9999px' }}
                />
                {status === 'error' && errorMsg && (
                  <p className="mb-4 text-sm text-red-600">{errorMsg}</p>
                )}
                <button
                  type="submit"
                  disabled={status === 'loading'}
                  className="w-full px-6 py-3 rounded-lg bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap disabled:opacity-70"
                >
                  {status === 'loading' ? '提交中...' : '发送留言'}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}